import { Simple } from "lib/@components/simple"
import { getKey } from "lib/@components/get-key"
import { Frag } from "lib/@components/slot/frag"
import { useRefresh } from "lib/@hooks/useRefresh"
import { pick } from "lib/pick"
import { Bound } from "lib/@components/binding/Bound"
import { createEvent } from "library/local-events/index.mjs"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useEffect, useRef, useState } from "react"

export const EventRepeatPopulate = createEvent("EventRepeatPopulate")

export function createEventRepeat(name) {
    return {
        Item: function Item({ children, deps }) {
            return (
                <EventItem name={name} deps={deps}>
                    {children}
                </EventItem>
            )
        },
        Repeat: function Repeat(props) {
            return <EventRepeat name={name} {...props} />
        },
        Items: function Items() {
            return <EventItems name={name} />
        },
        items: function items() {
            const items = []
            EventRepeatPopulate(name).raise({ items })
            return items
        },
        useItems: function useItems(...deps) {
            const [items, setItems] = useState([])
            useEffect(() => {
                setTimeout(() => {
                    const items = []
                    EventRepeatPopulate(name).raise({ items })
                    setItems(items)
                })
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, deps)
            return items
        },
    }
}

export function EventItems({ name }) {
    const items = []
    EventRepeatPopulate(name).raise({ items })
    return items
}

export function EventItem({ name, children, deps }) {
    const currentChildren = useRef()

    const context = useBoundContext()
    currentChildren.current = { children, context }
    EventRepeatPopulate(name).useEvent(insertItem, deps ? deps : [Date.now()])
    return null

    function insertItem({ items }) {
        items.push(<Bound {...currentChildren.current.context}>{currentChildren.current.children}</Bound>)
    }
}

export function EventRepeat({
    collection = [],
    name,
    list = collection,
    children,
    item = children?.type ? children : <Simple />,
    itemFn = () => false,
    pass = "item",
    keyFn = getKey,
    fallback = <Frag />,
    bind,
}) {
    const refresh = useRefresh()
    const context = useBoundContext()
    if (typeof keyFn === "string") keyFn = pick(keyFn)

    if (list instanceof Error) {
        console.warn("Attempting to iterate over an error", list)
        return null
    }

    if (!Array.isArray(list) && typeof list === "object" && list) {
        list = Object.entries(list)
    }
    EventRepeatPopulate(name).useEvent(getCurrentList, [list, item])
    return null

    function getCurrentList({ items }) {
        const myItems = !list?.length
            ? [fallback]
            : list?.map((iterated, index, ar) => {
                  const key = keyFn(iterated, index)
                  if (bind) {
                      return (
                          <Bound {...context} key={key} target={iterated}>
                              {itemFn(iterated, index, ar) || (
                                  <item.type
                                      remove={() => remove(iterated)}
                                      {...{
                                          ...item.props,
                                          [pass]: iterated,
                                          previous: ar[index - 1],
                                          index,
                                          last: list.length === index + 1,
                                      }}
                                  />
                              )}
                          </Bound>
                      )
                  }
                  const result = itemFn(iterated, index, ar)
                  if (typeof item.type === "string" && !result) {
                      return <item.type key={key} {...item.props} />
                  }
                  return (
                      <Bound {...context} key={key}>
                          {result || (
                              <item.type
                                  remove={() => remove(iterated)}
                                  {...{
                                      ...item.props,
                                      [pass]: iterated,
                                      previous: ar[index - 1],
                                      next: ar[index + 1],
                                      index,
                                      last: list.length === index + 1,
                                  }}
                              />
                          )}
                      </Bound>
                  )
              })
        items.push(...myItems)
    }

    function remove(item) {
        const idx = list.indexOf(item)
        if (idx >= 0) {
            list.splice(idx, 1)
            refresh()
        }
    }
}
